import { useEffect, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";
import {
  dateKey,
  eachDayOfInterval,
  endOfWeek,
  formatDateShort,
  isToday,
  startOfWeek,
} from "@/lib/date";
import { LivePulse } from "@/components/estate";
import {
  NOMINAL_VIEWING_MINUTES,
  type Appointment,
  type AppointmentStatus,
  type BrokerSummary,
} from "@/features/appointment/types";
import type { Lead } from "@/features/lead/types";

interface AppointmentWeekViewProps {
  anchorDate: Date;
  appointments: Appointment[];
  leads: Lead[];
  brokers: BrokerSummary[];
  onSelectAppointment: (appointment: Appointment) => void;
}

const DAY_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"] as const;
const HOUR_HEIGHT_PX = 50;
// Full 24-hour grid so appointments at any time of day land on the grid —
// the old 8–18 window pushed early-morning / late-evening ones off it.
const START_HOUR = 0;
const END_HOUR = 23;
const HOURS = Array.from(
  { length: END_HOUR - START_HOUR + 1 },
  (_, i) => START_HOUR + i,
);
// The grid is taller than a workday now, so the time column scrolls. We
// show ~12 hours at once and start it on the morning so the working day
// is visible without scrolling; the night hours are a scroll away.
const VISIBLE_HOURS = 12;
const INITIAL_SCROLL_HOUR = 7;

type Tone = "ok" | "accent" | "warn" | "default";

function toneFor(status: AppointmentStatus): Tone {
  switch (status) {
    case "COMPLETED":
      return "ok";
    case "SCHEDULED":
      return "accent";
    case "NO_SHOW":
      return "warn";
    case "CANCELED":
    default:
      return "default";
  }
}

const TONE_CLASS: Record<Tone, { bg: string; border: string; leftStripe: string }> = {
  ok: {
    bg: "bg-success-soft/70",
    border: "border-success/30",
    leftStripe: "border-l-success",
  },
  accent: {
    bg: "bg-accent/80",
    border: "border-accent-foreground/30",
    leftStripe: "border-l-accent-foreground",
  },
  warn: {
    bg: "bg-warning-soft/70",
    border: "border-warning/30",
    leftStripe: "border-l-warning",
  },
  default: {
    bg: "bg-muted",
    border: "border-border",
    leftStripe: "border-l-ink-faint",
  },
};

export function AppointmentWeekView({
  anchorDate,
  appointments,
  leads,
  brokers,
  onSelectAppointment,
}: AppointmentWeekViewProps) {
  const days = useMemo(() => {
    const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
    const end = endOfWeek(anchorDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [anchorDate]);

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const appointment of appointments) {
      const key = dateKey(appointment.scheduledAt);
      if (key === "") continue;
      const existing = map.get(key);
      if (existing) existing.push(appointment);
      else map.set(key, [appointment]);
    }
    return map;
  }, [appointments]);

  const leadById = useMemo(() => new Map(leads.map((l) => [l.id, l])), [leads]);
  const brokerById = useMemo(() => new Map(brokers.map((b) => [b.id, b])), [brokers]);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Land the scroll on the morning once, on mount. Empty deps so paging
  // between weeks (same component instance) doesn't yank the user's scroll.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = INITIAL_SCROLL_HOUR * HOUR_HEIGHT_PX;
    }
  }, []);

  const totalGridHeight = (END_HOUR - START_HOUR + 1) * HOUR_HEIGHT_PX;
  const now = new Date();
  const nowMin = (now.getHours() - START_HOUR) * 60 + now.getMinutes();
  const nowTopPx =
    nowMin > 0 && nowMin < (END_HOUR - START_HOUR + 1) * 60
      ? (nowMin / 60) * HOUR_HEIGHT_PX
      : null;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-[var(--shadow-soft)]">
      <div className="grid grid-cols-[60px_repeat(7,_1fr)] border-b border-border">
        <div />
        {days.map((day, i) => {
          const todayDay = isToday(day);
          return (
            <div
              key={dateKey(day)}
              className={cn(
                "border-l border-border py-2.5 text-center",
                todayDay ? "bg-accent/60" : "",
              )}
            >
              <div
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-wider",
                  todayDay ? "text-accent-foreground" : "text-muted-foreground",
                )}
              >
                {DAY_LABELS[i] ?? formatDateShort(day)}
              </div>
              <div
                className={cn(
                  "mt-0.5 font-mono text-[20px] font-bold leading-none tabular-nums tracking-tight",
                  todayDay ? "text-accent-foreground" : "text-foreground",
                )}
              >
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      <div
        ref={scrollRef}
        className="relative grid grid-cols-[60px_repeat(7,_1fr)] overflow-auto"
        style={{ maxHeight: VISIBLE_HOURS * HOUR_HEIGHT_PX }}
      >
        <div className="pt-2">
          {HOURS.map((h) => (
            <div
              key={h}
              style={{ height: HOUR_HEIGHT_PX }}
              className="pr-2 text-right font-mono text-[11px] text-ink-faint"
            >
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {days.map((day) => {
          const key = dateKey(day);
          const dayAppointments = byDay.get(key) ?? [];
          const todayDay = isToday(day);
          return (
            <div
              key={key}
              className="relative border-l border-border pt-2"
              style={{ minHeight: totalGridHeight + 8 }}
            >
              {HOURS.map((h) => (
                <div
                  key={h}
                  style={{ height: HOUR_HEIGHT_PX }}
                  className="border-b border-border"
                />
              ))}

              {dayAppointments.map((appointment) => {
                const startDate = new Date(appointment.scheduledAt);
                const startMin =
                  (startDate.getHours() - START_HOUR) * 60 + startDate.getMinutes();
                const topPx = Math.max(0, (startMin / 60) * HOUR_HEIGHT_PX) + 8;
                // Viewings have no fixed length; every block is a fixed
                // nominal height — the start time is what matters.
                const heightPx = Math.max(
                  20,
                  (NOMINAL_VIEWING_MINUTES / 60) * HOUR_HEIGHT_PX - 2,
                );
                const tone = TONE_CLASS[toneFor(appointment.status)];
                const lead =
                  appointment.leadId !== null
                    ? leadById.get(appointment.leadId)
                    : undefined;
                const broker = brokerById.get(appointment.brokerId);
                const customerName = lead?.customer.fullName ?? null;
                const title =
                  lead?.interestedPropertyBuildingName ??
                  customerName ??
                  broker?.fullName ??
                  "Lịch hẹn";
                const meta =
                  customerName !== null && customerName !== title
                    ? customerName
                    : (broker?.fullName ?? "—");
                const time = startDate.toLocaleTimeString("vi-VN", {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const endTs =
                  startDate.getTime() + NOMINAL_VIEWING_MINUTES * 60_000;
                const active =
                  appointment.status === "SCHEDULED" &&
                  Date.now() >= startDate.getTime() &&
                  Date.now() <= endTs;

                return (
                  <button
                    type="button"
                    key={appointment.id}
                    onClick={() => onSelectAppointment(appointment)}
                    style={{ top: topPx, height: heightPx }}
                    className={cn(
                      "absolute left-1 right-1 overflow-hidden rounded-md border border-l-[3px] px-1.5 py-1 text-left transition-shadow",
                      tone.bg,
                      tone.border,
                      tone.leftStripe,
                      "hover:shadow-[var(--shadow-soft)]",
                    )}
                  >
                    <div className="flex items-center gap-1">
                      {active ? <LivePulse tone="success" /> : null}
                      <span className="truncate text-[11px] font-semibold leading-tight">
                        {title}
                      </span>
                    </div>
                    <div className="mt-1 truncate font-mono text-[11px] text-muted-foreground">
                      {time} · {meta}
                    </div>
                  </button>
                );
              })}

              {todayDay && nowTopPx !== null ? (
                <div
                  className="pointer-events-none absolute inset-x-0 z-10"
                  style={{ top: nowTopPx + 8 }}
                >
                  <div className="relative h-px bg-primary">
                    <span className="absolute -left-1 -top-[3px] size-1.5 rounded-full bg-primary" />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
